import { lazy, type ReactElement } from 'react';
import { matchPath } from 'react-router-dom';
import ProtectedRoute, { type RoutePrefetch } from 'features/identity/public/ProtectedRoute';
import { prefetchReportOpen } from 'features/report-workspace/public/workspaceMetadata';
import { primeReportCatalog } from 'features/reports/public/reportCatalog';
import { APP_PATHS } from 'app/router/paths';

const UserManagementPage = lazy(() => import('pages/administration/UserManagementPage'));
const SubscriptionPage = lazy(() => import('pages/billing/SubscriptionPage'));
const BusinessHomePage = lazy(() => import('pages/business-home/BusinessHomePage'));
const ConnectionsPage = lazy(() => import('pages/connections/ConnectionsPage'));
const ConnectionCredentialsPage = lazy(() => import('pages/connections/ConnectionCredentialsPage'));
const loadHomePage = () => import('pages/home/HomePage');
const HomePage = lazy(loadHomePage);
const HtmlPagePreview = lazy(() => import('features/html-reporting/PagePreview'));
const ForgotPasswordPage = lazy(() => import('pages/identity/password-reset/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('pages/identity/password-reset/ResetPasswordPage'));
const LoginPage = lazy(() => import('pages/identity/login/LoginPage'));
const RegisterPage = lazy(() => import('pages/identity/register/RegisterPage'));
const DataDeletionPage = lazy(() => import('pages/legal/DataDeletionPage'));
const PrivacyPolicyPage = lazy(() => import('pages/legal/PrivacyPolicyPage'));
const UnityFrontDoorPage = lazy(() => import('pages/public/UnityFrontDoorPage'));
const loadReportWorkspacePage = () => import('pages/report-workspace/ReportWorkspacePage');
const ReportWorkspacePage = lazy(loadReportWorkspacePage);
const WorkspacePage = lazy(() => import('pages/workspaces/WorkspacePage'));
const OrganizationPage = lazy(() => import('pages/collaboration/OrganizationPage'));
const ApiAccessPage = lazy(() => import('pages/identity/api-access/ApiAccessPage'));
const ConnectAppPage = lazy(() => import('pages/identity/connect/ConnectAppPage'));
const DocumentationPage = lazy(() => import('pages/documentation/DocumentationPage'));
const NotFoundPage = lazy(() => import('pages/public/NotFoundPage'));
const PublicLinkPage = lazy(() => import('features/public-pages/PublicLinkPage'));

export interface AppRouteDefinition {
    path: string;
    element: ReactElement;
}

const protectedPage = (element: ReactElement, prefetch?: RoutePrefetch): ReactElement => (
    <ProtectedRoute prefetch={prefetch}>{element}</ProtectedRoute>
);

// Report open: load the workspace code, its metadata and the first page's shell
// manifest while the session and subscription checks are still in flight.
const prefetchReportWorkspace: RoutePrefetch = ({ userId, pathname, search }) => {
    const reportId = matchPath(APP_PATHS.reportWorkspace, pathname)?.params.reportId;
    if (!reportId) return;
    void loadReportWorkspacePage().catch(() => undefined);
    prefetchReportOpen(reportId, userId, new URLSearchParams(search).get('page'));
};

// Home: the report list and the page code load alongside the subscription check.
const prefetchHome: RoutePrefetch = ({ userId }) => {
    void loadHomePage().catch(() => undefined);
    primeReportCatalog(userId);
};

export const APP_ROUTES: readonly AppRouteDefinition[] = [
    { path: '/html-preview', element: <HtmlPagePreview /> },
    // Bookmark compatibility only: these open the home page.
    { path: '/unity2.0', element: protectedPage(<HomePage />, prefetchHome) },
    { path: '/Unity2', element: protectedPage(<HomePage />, prefetchHome) },
    { path: APP_PATHS.frontDoor, element: <UnityFrontDoorPage /> },
    { path: APP_PATHS.publicLink, element: <PublicLinkPage /> },
    { path: APP_PATHS.businessHome, element: <BusinessHomePage /> },
    { path: APP_PATHS.register, element: <RegisterPage /> },
    { path: APP_PATHS.login, element: <LoginPage /> },
    { path: APP_PATHS.forgotPassword, element: <ForgotPasswordPage /> },
    { path: APP_PATHS.resetPassword, element: <ResetPasswordPage /> },
    { path: APP_PATHS.privacyPolicy, element: <PrivacyPolicyPage /> },
    { path: APP_PATHS.dataDeletion, element: <DataDeletionPage /> },
    { path: APP_PATHS.subscription, element: <SubscriptionPage /> },
    { path: APP_PATHS.administration, element: <UserManagementPage /> },
    { path: APP_PATHS.connections, element: protectedPage(<ConnectionsPage />) },
    // An agent's connection links its owner here to type its secrets; like /connect, no subscription is needed.
    { path: APP_PATHS.connectionCredentials, element: <ProtectedRoute requireSubscription={false}><ConnectionCredentialsPage /></ProtectedRoute> },
    { path: APP_PATHS.home, element: protectedPage(<HomePage />, prefetchHome) },
    { path: APP_PATHS.workspaces, element: protectedPage(<WorkspacePage />) },
    { path: APP_PATHS.organization, element: <ProtectedRoute requireSubscription={false}><OrganizationPage /></ProtectedRoute> },
    { path: APP_PATHS.apiAccess, element: <ProtectedRoute requireSubscription={false}><ApiAccessPage /></ProtectedRoute> },
    { path: APP_PATHS.connectApp, element: <ProtectedRoute requireSubscription={false}><ConnectAppPage /></ProtectedRoute> },
    { path: APP_PATHS.docs, element: <ProtectedRoute requireSubscription={false}><DocumentationPage /></ProtectedRoute> },
    { path: APP_PATHS.docsArticle, element: <ProtectedRoute requireSubscription={false}><DocumentationPage /></ProtectedRoute> },
    { path: APP_PATHS.reportWorkspace, element: protectedPage(<ReportWorkspacePage />, prefetchReportWorkspace) },
    { path: '*', element: <NotFoundPage signedInHome={APP_PATHS.home} signedOutHome={APP_PATHS.frontDoor} /> },
] as const;
